import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';

import { ApiError } from '../../lib/apiFetch.js';
import { ALLOWED_JOB_STATUSES, type JobStatus } from './contractsMirror.js';
import { useJobQuery, useUpdateJobMutation } from './useJobQuery.js';

const routeApi = getRouteApi('/jobs/$id/edit');

const INPUT_CLASS =
  'h-11 w-full rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const TEXTAREA_CLASS =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const LABEL_CLASS = 'mb-1 block font-medium text-foreground text-sm';

export function JobEditRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = routeApi.useParams();
  const jobId = Number(id);

  const query = useJobQuery(jobId);
  const updateMutation = useUpdateJobMutation(jobId);

  const [initializedForJobId, setInitializedForJobId] = useState<number | null>(null);
  const [jobName, setJobName] = useState('');
  const [jobCode, setJobCode] = useState('');
  const [clientName, setClientName] = useState('');
  const [soldBy, setSoldBy] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [status, setStatus] = useState<JobStatus | ''>('');
  const [isOverhead, setIsOverhead] = useState(false);
  const [contractAmount, setContractAmount] = useState('');
  const [commissionPercent, setCommissionPercent] = useState('');
  const [retainagePercent, setRetainagePercent] = useState('');
  const [startDate, setStartDate] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [jobCodeError, setJobCodeError] = useState<string | null>(null);

  useEffect(() => {
    const job = query.data?.job;
    if (!job || initializedForJobId === jobId) return;

    setJobName(job.jobName ?? '');
    setJobCode(job.jobCode ?? '');
    setClientName(job.clientName ?? '');
    setSoldBy(job.soldBy ?? '');
    setJobDescription(job.jobDescription ?? '');
    setStatus((job.status as JobStatus | null) ?? '');
    setIsOverhead(Boolean(job.isOverhead));
    setContractAmount(
      job.contractAmount !== undefined && job.contractAmount !== null
        ? String(job.contractAmount)
        : '',
    );
    setCommissionPercent(
      job.commissionPercent !== undefined && job.commissionPercent !== null
        ? String(job.commissionPercent)
        : '',
    );
    setRetainagePercent(
      job.retainagePercent !== undefined && job.retainagePercent !== null
        ? String(job.retainagePercent)
        : '',
    );
    setStartDate(job.startDate ?? '');
    setInitializedForJobId(jobId);
  }, [query.data?.job, initializedForJobId, jobId]);

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  const job = query.data?.job;
  if (query.isError || !job) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('jobs:error.detailHeading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('jobs:error.detailBody')}</p>
        <Button asChild variant="secondary">
          <Link to="/jobs">{t('jobs:actions.backToJobs')}</Link>
        </Button>
      </section>
    );
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSuccessMessage(null);
    setJobCodeError(null);

    updateMutation.mutate(
      {
        jobName: jobName.trim() || null,
        jobCode: jobCode.trim() || null,
        clientName: clientName.trim() || null,
        soldBy: soldBy.trim() || null,
        jobDescription: jobDescription.trim() || null,
        status: status || null,
        isOverhead,
        contractAmount: contractAmount.trim() ? Number(contractAmount) : null,
        commissionPercent: commissionPercent.trim() ? Number(commissionPercent) : null,
        retainagePercent: retainagePercent.trim() ? Number(retainagePercent) : null,
        startDate: startDate || null,
      },
      {
        onSuccess: () => {
          setSuccessMessage(t('jobs:edit.saveSuccess'));
          setTimeout(() => {
            void navigate({ to: '/jobs/$id', params: { id: String(jobId) } });
          }, 200);
        },
        onError: (error) => {
          if (error instanceof ApiError && error.message === 'job_code_exists') {
            setJobCodeError(t('jobs:form.jobCodeExists'));
          }
        },
      },
    );
  }

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <h1 className="font-display font-semibold text-3xl text-foreground">
          {t('jobs:edit.heading')}
        </h1>
        <Button asChild variant="secondary">
          <Link to="/jobs/$id" params={{ id: String(jobId) }}>
            {t('jobs:edit.cancel')}
          </Link>
        </Button>
      </header>

      <form
        onSubmit={submit}
        className="max-w-3xl space-y-4 rounded-md border border-border bg-card p-6"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="job-name" className={LABEL_CLASS}>
              {t('jobs:edit.jobName')}
            </label>
            <input
              id="job-name"
              value={jobName}
              onChange={(event) => setJobName(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="job-code" className={LABEL_CLASS}>
              {t('jobs:edit.jobCode')}
            </label>
            <input
              id="job-code"
              value={jobCode}
              onChange={(event) => setJobCode(event.target.value)}
              aria-invalid={jobCodeError ? true : undefined}
              aria-describedby={jobCodeError ? 'job-code-error' : undefined}
              className={INPUT_CLASS}
            />
            {jobCodeError ? (
              <p id="job-code-error" role="alert" className="mt-1 text-destructive text-sm">
                {jobCodeError}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="job-status" className={LABEL_CLASS}>
              {t('jobs:edit.status')}
            </label>
            <select
              id="job-status"
              value={status}
              onChange={(event) => setStatus(event.target.value as JobStatus)}
              className={INPUT_CLASS}
            >
              {ALLOWED_JOB_STATUSES.map((statusOption) => (
                <option key={statusOption} value={statusOption}>
                  {statusOption}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="job-client-name" className={LABEL_CLASS}>
              {t('jobs:edit.clientName')}
            </label>
            <input
              id="job-client-name"
              value={clientName}
              onChange={(event) => setClientName(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="job-sold-by" className={LABEL_CLASS}>
              {t('jobs:edit.soldBy')}
            </label>
            <input
              id="job-sold-by"
              value={soldBy}
              onChange={(event) => setSoldBy(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="job-contract-amount" className={LABEL_CLASS}>
              {t('jobs:edit.contractAmount')}
            </label>
            <input
              id="job-contract-amount"
              type="number"
              min="0"
              step="0.01"
              value={contractAmount}
              onChange={(event) => setContractAmount(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="job-commission" className={LABEL_CLASS}>
              {t('jobs:form.commissionPercent')}
            </label>
            <input
              id="job-commission"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={commissionPercent}
              onChange={(event) => setCommissionPercent(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="job-retainage" className={LABEL_CLASS}>
              {t('jobs:form.retainagePercent')}
            </label>
            <input
              id="job-retainage"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={retainagePercent}
              onChange={(event) => setRetainagePercent(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="job-start-date" className={LABEL_CLASS}>
              {t('jobs:edit.startDate')}
            </label>
            <input
              id="job-start-date"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="inline-flex items-center gap-2 font-medium text-foreground text-sm">
              <input
                type="checkbox"
                checked={isOverhead}
                onChange={(event) => setIsOverhead(event.target.checked)}
                className="size-4 rounded border border-border bg-background text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              />
              {t('jobs:edit.isOverhead')}
            </label>
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="job-description" className={LABEL_CLASS}>
              {t('jobs:edit.jobDescription')}
            </label>
            <textarea
              id="job-description"
              value={jobDescription}
              onChange={(event) => setJobDescription(event.target.value)}
              rows={4}
              className={TEXTAREA_CLASS}
            />
          </div>
        </div>

        {updateMutation.isError ? (
          <p role="alert" className="text-destructive text-sm">
            {t('jobs:edit.saveError')}
          </p>
        ) : null}
        {successMessage ? (
          <p role="status" className="text-secondary-foreground text-sm">
            {successMessage}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={updateMutation.isPending}>
            {t('jobs:edit.submit')}
          </Button>
          <Button asChild variant="secondary">
            <Link to="/jobs/$id" params={{ id: String(jobId) }}>
              {t('jobs:edit.cancel')}
            </Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
